import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Heart, Send, Flame, Clock, Trophy, ChevronDown, Trash2, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, LoadingState } from "@/components/states";
import { FamilyReports } from "@/components/FamilyReports";
import { FamilyCompare } from "@/components/FamilyCompare";
import { ChildNotifyPrefs } from "@/components/ChildNotifyPrefs";
import { ParentInviteCard, RedeemInviteCard } from "@/components/FamilyInvite";
import { getFamilyDigest } from "@/lib/family.functions";
import type { ChildMetrics } from "@/lib/family";
import { supabase } from "@/integrations/supabase/client";
import {
  getChildOverview,
  getFamilyClasses,
  listParentLinks,
  removeParentLink,
  requestParentLink,
  respondParentLink,
} from "@/lib/teacher-community.functions";


export const Route = createFileRoute("/_app/family")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Area famiglia — Lumen" },
      {
        name: "description",
        content: "Collega i profili dei tuoi figli e segui studio, compiti, voti e streak da un'unica dashboard.",
      },
      { property: "og:title", content: "Area famiglia — Lumen" },
      { property: "og:description", content: "Segui i progressi di studio della tua famiglia su Lumen." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: FamilyPage,
});

type Child = { id: string; studentId: string; status: string; name: string; xp: number; level: number; streak: number };
type Req = { id: string; parentId: string; status: string; name: string };
type Overview = Awaited<ReturnType<typeof getChildOverview>>;

function Stat({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="min-w-0 rounded-2xl bg-muted/50 p-3 text-center">
      <div className="mx-auto mb-1 flex h-8 w-8 items-center justify-center rounded-full bg-background text-primary">
        {icon}
      </div>
      <p className="truncate text-sm font-semibold">{value}</p>
      <p className="truncate text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}

function FamilyClassesSection({ onOpenChild }: { onOpenChild: (studentId: string) => void }) {
  const classesFn = useServerFn(getFamilyClasses);
  const [rows, setRows] = useState<Awaited<ReturnType<typeof getFamilyClasses>>["classes"]>([]);

  useEffect(() => {
    void (async () => {
      try {
        const res = await classesFn({ data: {} } as never);
        setRows(res.classes);
      } catch {
        /* nessuna classe o sessione non pronta */
      }
    })();
  }, [classesFn]);

  if (rows.length === 0) return null;

  const dayName = (d: number) =>
    ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"][d % 7] ?? "";

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold">Le classi dei tuoi figli</h2>
      {rows.map((c) => (
        <Card key={c.id} className="card-soft min-w-0 space-y-3 border-0 p-4">
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{c.name}</p>
              <p className="truncate text-xs text-muted-foreground">{c.subject}</p>
            </div>
            <Badge variant="secondary">{c.children.length} iscritti</Badge>
          </div>

          <div>
            <p className="mb-1 text-xs font-semibold text-muted-foreground">Orario</p>
            {c.schedule.length === 0 ? (
              <p className="text-xs text-muted-foreground">Orario non ancora inserito.</p>
            ) : (
              <ul className="space-y-1">
                {c.schedule.slice(0, 8).map((s) => (
                  <li key={s.id} className="flex min-w-0 justify-between gap-2 text-xs">
                    <span className="truncate">
                      {dayName(s.day)} · {s.subject}
                      {s.room ? ` · ${s.room}` : ""}
                    </span>
                    <span className="shrink-0 text-muted-foreground">
                      {s.start.slice(0, 5)}–{s.end.slice(0, 5)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="mb-1 text-xs font-semibold text-muted-foreground">Compiti assegnati</p>
            {c.assignments.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nessun compito in arrivo.</p>
            ) : (
              <ul className="space-y-1">
                {c.assignments.map((a) => (
                  <li key={a.id} className="flex min-w-0 justify-between gap-2 text-xs">
                    <span className="truncate">{a.title}</span>
                    <span className="shrink-0 text-muted-foreground">
                      {a.dueDate ? new Date(a.dueDate).toLocaleDateString("it-IT") : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {c.children.map((k) => (
              <Button
                key={k.studentId}
                size="sm"
                variant="outline"
                className="min-h-9"
                onClick={() => onOpenChild(k.studentId)}
              >
                Apri scheda di {k.name}
              </Button>
            ))}
          </div>
        </Card>
      ))}
    </section>
  );
}

function ChildCard({
  child,
  prefs,
  focused = false,
  onRemove,
}: {
  child: Child;
  prefs?: unknown;
  focused?: boolean;
  onRemove: (linkId: string) => void;
}) {
  const overviewFn = useServerFn(getChildOverview);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(false);


  const load = useCallback(async () => {
    if (child.status !== "accepted") return;
    setLoading(true);
    try {
      setData((await overviewFn({ data: { studentId: child.studentId } })) as Overview);
    } catch {
      toast.error("Dati non disponibili");
    } finally {
      setLoading(false);
    }
  }, [child.status, child.studentId, overviewFn]);

  useEffect(() => {
    if (focused) setOpen(true);
  }, [focused]);

  useEffect(() => {
    if (open && !data) void load();
  }, [open, data, load]);

  const max = Math.max(30, ...((data?.perDay ?? []).map((d) => d.minutes) || [0]));

  return (
    <Card className="card-soft min-w-0 space-y-3 border-0 p-4">
      <div className="flex min-w-0 items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          aria-expanded={open}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
            {child.name.slice(0, 1).toUpperCase()}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold">{child.name}</span>
            <span className="block truncate text-xs text-muted-foreground">
              {child.status === "accepted"
                ? `Livello ${child.level} · ${child.xp} XP · ${child.streak} giorni`
                : "In attesa di conferma"}
            </span>
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-1">
          <Badge variant={child.status === "accepted" ? "secondary" : "outline"}>
            {child.status === "accepted" ? "Collegato" : "In attesa"}
          </Badge>
          <Button
            size="icon"
            variant="ghost"
            className="min-h-9 min-w-9"
            aria-label="Rimuovi collegamento"
            onClick={() => onRemove(child.id)}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
          {child.status === "accepted" && (
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
              aria-hidden
            />
          )}
        </div>
      </div>

      {open && child.status === "accepted" && (
        <div className="space-y-3 border-t pt-3">
          {loading && !data ? (
            <LoadingState />
          ) : data ? (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat icon={<Clock className="h-4 w-4" aria-hidden />} value={`${data.weekMinutes} min`} label="Ultimi 7 giorni" />
                <Stat icon={<Flame className="h-4 w-4" aria-hidden />} value={`${data.streak}`} label="Streak" />
                <Stat
                  icon={<Trophy className="h-4 w-4" aria-hidden />}
                  value={data.quizAvg != null ? `${data.quizAvg}%` : "—"}
                  label="Media quiz"
                />
                <Stat
                  icon={<Trophy className="h-4 w-4" aria-hidden />}
                  value={data.gradeAvg != null ? `${data.gradeAvg}` : "—"}
                  label="Media voti"
                />
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold text-muted-foreground">Studio della settimana</p>
                <div className="flex h-20 items-end gap-1.5">
                  {data.perDay.map((d) => (
                    <div key={d.day} className="flex min-w-0 flex-1 flex-col items-center gap-1">
                      <div
                        className="w-full rounded-t-md bg-primary/70"
                        style={{ height: `${Math.max(4, (d.minutes / max) * 60)}px` }}
                        title={`${d.minutes} min`}
                      />
                      <span className="text-[10px] text-muted-foreground">{d.day.slice(8)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold text-muted-foreground">Compiti in arrivo</p>
                {data.homework.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nessun compito in scadenza.</p>
                ) : (
                  <ul className="space-y-1">
                    {data.homework.map((h) => (
                      <li key={h.id} className="flex min-w-0 items-center justify-between gap-2 text-sm">
                        <span className="min-w-0 truncate">
                          {h.subject ? `${h.subject} · ` : ""}
                          {h.title}
                        </span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {h.dueDate}
                          {h.done ? " ✓" : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold text-muted-foreground">Consegne dei professori</p>
                {data.assignments.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nessuna consegna assegnata.</p>
                ) : (
                  <ul className="space-y-1">
                    {data.assignments.map((a) => (
                      <li key={a.id} className="flex min-w-0 items-center justify-between gap-2 text-sm">
                        <span className="min-w-0 truncate">
                          {a.className} · {a.title}
                          {a.dueDate ? (
                            <span className="text-xs text-muted-foreground"> · {a.dueDate}</span>
                          ) : null}
                        </span>
                        <Badge
                          variant={a.status === "graded" ? "secondary" : a.status === "submitted" ? "outline" : "outline"}
                          className="shrink-0"
                        >
                          {a.status === "graded"
                            ? `Voto ${a.score}/${a.maxScore}`
                            : a.status === "submitted"
                              ? "Consegnato"
                              : "Da fare"}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold text-muted-foreground">Obiettivi giornalieri</p>
                {data.goals.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nessun obiettivo impostato.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.goals.map((g) => {
                      const pct = g.target > 0 ? Math.min(100, Math.round((g.done / g.target) * 100)) : g.completed ? 100 : 0;
                      return (
                        <li key={g.id} className="min-w-0">
                          <div className="flex min-w-0 items-center justify-between gap-2 text-sm">
                            <span className="min-w-0 truncate">{g.title}</span>
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {g.target > 0 ? `${g.done}/${g.target} min` : g.completed ? "Completato" : "In corso"}
                            </span>
                          </div>
                          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <ChildNotifyPrefs linkId={child.id} value={prefs} />


              <FamilyReports studentId={child.studentId} />


              <Button variant="outline" size="sm" className="min-h-9" onClick={() => void load()}>
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
                Aggiorna
              </Button>
            </>
          ) : null}
        </div>
      )}
    </Card>
  );
}

function FamilyPage() {
  const { t } = useTranslation();
  const listFn = useServerFn(listParentLinks);
  const requestFn = useServerFn(requestParentLink);
  const respondFn = useServerFn(respondParentLink);
  const removeFn = useServerFn(removeParentLink);
  const digestFn = useServerFn(getFamilyDigest);

  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState<Child[]>([]);
  const [requests, setRequests] = useState<Req[]>([]);
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(7);
  const [digest, setDigest] = useState<Awaited<ReturnType<typeof getFamilyDigest>> | null>(null);
  const [inviteFromLink, setInviteFromLink] = useState<string | undefined>(undefined);
  const [focusChild, setFocusChild] = useState<string | null>(null);

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("invite");
    if (code) setInviteFromLink(code);
  }, []);

  const refresh = useCallback(async () => {
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) {
      setLoading(false);
      return;
    }
    try {
      const res = await listFn({ data: {} } as never);
      setChildren((res.children ?? []) as Child[]);
      setRequests((res.requests ?? []) as Req[]);
      setDigest(await digestFn({ data: { days } }));
    } catch {
      /* silenzioso: sessione non pronta */
    } finally {
      setLoading(false);
    }
  }, [listFn, digestFn, days]);


  useEffect(() => {
    void refresh();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") void refresh();
    });
    return () => sub.subscription.unsubscribe();
  }, [refresh]);

  const send = async () => {
    if (!username.trim()) return;
    setBusy(true);
    try {
      await requestFn({ data: { username: username.trim() } });
      toast.success(t("teacher.parentArea.sent", { defaultValue: "Richiesta inviata" }));
      setUsername("");
      await refresh();
    } catch {
      toast.error(t("teacher.parentArea.notFound", { defaultValue: "Studente non trovato" }));
    } finally {
      setBusy(false);
    }
  };

  const respond = async (linkId: string, accept: boolean) => {
    await respondFn({ data: { linkId, accept } });
    await refresh();
  };

  const remove = async (linkId: string) => {
    await removeFn({ data: { linkId } });
    toast.success("Collegamento rimosso");
    await refresh();
  };

  const accepted = children.filter((c) => c.status === "accepted");

  return (
    <main className="space-y-5 px-4 py-6">
      <PageHeader
        icon={<Heart className="h-5 w-5" aria-hidden />}
        title="Area famiglia"
        subtitle="Collega uno o più figli e segui studio, compiti e risultati."
      />

      {accepted.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          <Stat icon={<Heart className="h-4 w-4" aria-hidden />} value={`${accepted.length}`} label="Figli collegati" />
          <Stat
            icon={<Flame className="h-4 w-4" aria-hidden />}
            value={`${Math.max(0, ...accepted.map((c) => c.streak))}`}
            label="Streak migliore"
          />
          <Stat
            icon={<Trophy className="h-4 w-4" aria-hidden />}
            value={`${accepted.reduce((a, c) => a + c.xp, 0)}`}
            label="XP totali"
          />
        </div>
      )}

      <ParentInviteCard onChanged={() => void refresh()} />

      <RedeemInviteCard initialCode={inviteFromLink} onLinked={() => void refresh()} />

      <details className="rounded-2xl bg-muted/40 p-3">
        <summary className="cursor-pointer text-sm font-semibold">Collega tramite username (alternativa)</summary>
        <div className="mt-3 space-y-2">
          <Label htmlFor="family-user">Username del figlio</Label>
          <div className="flex flex-wrap gap-2">
            <Input
              id="family-user"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="@username"
              className="min-w-0 flex-1"
            />
            <Button onClick={send} disabled={busy || !username.trim()} className="min-h-11">
              <Send className="mr-2 h-4 w-4" aria-hidden />
              Invia richiesta
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Con l'username il figlio deve accettare la richiesta dal proprio profilo.
          </p>
        </div>
      </details>

      {digest && digest.children.length > 1 && (
        <FamilyCompare
          children={digest.children as ChildMetrics[]}
          homework={digest.homework}
          days={days}
          onDaysChange={setDays}
        />
      )}

      {loading ? (
        <LoadingState />
      ) : (
        <>
          <FamilyClassesSection onOpenChild={setFocusChild} />

          <section className="space-y-2">
            <h2 className="text-sm font-semibold">I tuoi figli</h2>
            {children.length === 0 ? (
              <EmptyState title="Nessun figlio collegato" />
            ) : (
              children.map((c) => (
                <ChildCard
                  key={c.id}
                  child={c}
                  focused={focusChild === c.studentId}
                  prefs={digest?.children.find((d) => d.linkId === c.id)?.prefs}
                  onRemove={remove}
                />
              ))

            )}
          </section>


          <section className="space-y-2">
            <h2 className="text-sm font-semibold">Richieste ricevute</h2>
            {requests.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessuna richiesta.</p>
            ) : (
              requests.map((r) => (
                <Card key={r.id} className="card-soft flex min-w-0 flex-wrap items-center justify-between gap-2 border-0 p-4">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{r.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {r.status === "accepted" ? "Collegato" : "In attesa"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {r.status !== "accepted" && (
                      <Button size="sm" className="min-h-9" onClick={() => respond(r.id, true)}>
                        Accetta
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="min-h-9" onClick={() => respond(r.id, false)}>
                      {r.status === "accepted" ? "Revoca" : "Rifiuta"}
                    </Button>
                  </div>
                </Card>
              ))
            )}
          </section>
        </>
      )}
    </main>
  );
}
